import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {OfferData} from "types/Offer";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import OfferForm from "pages/Offer/Form";

function MutateOffer() {
    const {id} = useParams()
    const {data: offer, isLoading} = useQuery(
        ['offers', {id}],
        () => ngage.offers.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: OfferData) => ngage.offers.store(data))
    const update = useMutation((data: OfferData) => ngage.offers.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: OfferData, {setSubmitting}: FormikHelpers<OfferData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('offers.all')

        setSubmitting(false)
        navigate('/offers')
    }

    // Handle store mutation
    async function handleStore(values: OfferData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Offer Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Offer Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: OfferData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Offer Updated", `${values.name} has been updated`)
            await invalidate(['offers', {id}])
        } catch (error) {
            notify("error", "Offer Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Engagement': '/', 'Offers': '/offers', 'New Offer': '/offers/new'}
    const editBreadcrumbs = {'Engagement': '/', 'Offers': '/offers', 'Edit Offer': `/offers/${id}/edit`}

    return (
        <Page title={id ? "Edit Offer" : "New Offer"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <OfferForm offer={offer} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateOffer;